import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase, supabaseKey, supabaseUrl } from '../lib/supabase';
import { errorText } from '../lib/errors';
import { ErrorNote } from './ui';

/**
 * Whether this Supabase project has Google sign-in switched on (Authentication → Providers). The button only shows
 * where it is, so local, staging and the live site need no separate switch in the app.
 */
export function useGoogleEnabled() {
  return useQuery({
    queryKey: ['auth-providers'],
    queryFn: async () => {
      const r = await fetch(`${supabaseUrl}/auth/v1/settings`, { headers: { apikey: supabaseKey } });
      if (!r.ok) return false;
      const j = await r.json() as { external?: Record<string, boolean> };
      return !!j.external?.google;
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/**
 * Google refuses to sign in inside an app's built-in browser (Facebook, Instagram and other WebViews): "Error 403:
 * disallowed_useragent". There the button is replaced by a note to open the page in Chrome or Safari.
 */
const IN_APP = /FBAN|FBAV|FB_IAB|Instagram|Line\/|LinkedInApp|Snapchat|Twitter|; wv\)/i;

/** Where to land after Google: a path on this site only. */
export function safeNext(next: string | null | undefined): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** "Continue with Google", then "or" above the email field. Nothing while Google isn't switched on. */
export function GoogleSignIn({ next }: { next: string }) {
  const enabled = useGoogleEnabled();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!enabled.data) return null;
  const inApp = typeof navigator !== 'undefined' && IN_APP.test(navigator.userAgent);

  async function go() {
    setError(null);
    setBusy(true);
    const { error: e } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${location.origin}/auth/callback?next=${encodeURIComponent(safeNext(next))}`,
        // Family phones are shared: let people pick which Google account, every time.
        queryParams: { prompt: 'select_account' },
      },
    });
    // On success the browser is already on its way to Google.
    if (e) {
      setBusy(false);
      setError(errorText(e));
    }
  }

  return (
    <div className="gsign">
      {inApp ? (
        <p className="notice">Google sign-in doesn't work inside this app's browser. Open this page in Chrome or Safari to use it, or use
          your email below.</p>
      ) : (
        <button type="button" className="btn gbtn block lg" disabled={busy} onClick={() => void go()}>
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          {busy ? 'Opening Google…' : 'Continue with Google'}
        </button>
      )}
      <ErrorNote error={error} />
      <p className="gsign-or"><span>or with your email</span></p>
    </div>
  );
}
